import React, { useState, useRef, useEffect } from 'react';
import {
  IonIcon,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonText,
  IonTextarea,
} from '@ionic/react';
import {
  documentOutline,
  linkOutline,
  cameraOutline,
  closeOutline,
  folderOpenOutline,
} from 'ionicons/icons';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { FileAttachment } from '@/types/model/inspection-modal';
import { FileResponse } from '@/types/response/InspectionResultResponses';

// Add responsive styles
const attachmentStyles = `
  .attachment-responsive-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .attachment-label-wrapper {
    flex: 0 0 auto;
    min-width: 0;
    overflow: hidden;
  }
  
  .attachment-description-wrapper {
    flex: 1 1 auto;
  }
  
  @media (min-width: 768px) {
    .attachment-responsive-container {
      flex-direction: row;
      align-items: flex-start;
      gap: 12px;
    }
    
    .attachment-label-wrapper {
      flex: 1 1 40%;
      max-width: 300px;
      min-width: 0;
    }
    
    .attachment-description-wrapper {
      flex: 1 1 60%;
    }
  }
`;

// Inject styles if not already present
if (!document.querySelector('#attachment-responsive-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'attachment-responsive-styles';
  styleSheet.textContent = attachmentStyles;
  document.head.appendChild(styleSheet);
}

interface AttachmentComponentProps {
  itemId: number;
  index: number;
  attachment: FileAttachment;
  existingFiles: FileResponse[];
  readOnly?: boolean;
  onDelete: (itemId: number, index: number) => void;
  onFileUpdate: (itemId: number, index: number, file: File) => void;
  onLinkUpdate: (itemId: number, index: number, link: string) => void;
  onExistingFileSelect: (itemId: number, index: number, fileId: string) => void;
  onDescriptionUpdate: (itemId: number, index: number, description: string) => void;
}

const AttachmentComponent: React.FC<AttachmentComponentProps> = ({
  itemId,
  index,
  attachment,
  existingFiles,
  readOnly = false,
  onDelete,
  onFileUpdate,
  onLinkUpdate,
  onExistingFileSelect,
  onDescriptionUpdate,
}) => {
  const [description, setDescription] = useState(attachment.description || '');
  const [link, setLink] = useState(attachment.link || '');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDescription(attachment.description || '');
    setLink(attachment.link || '');
  }, [attachment.description, attachment.link, attachment.type, attachment.fileId, attachment.fileName]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onFileUpdate(itemId, index, file);
    }
  };

  const handleTakePhoto = async () => {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: true,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
      });

      if (image.dataUrl) {
        // Convert data URL to File object
        const response = await fetch(image.dataUrl);
        const blob = await response.blob();
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
        onFileUpdate(itemId, index, file);
      }
    } catch (error) {
      console.error('Error taking photo:', error);
    }
  };

  const handleExistingFileSelect = (fileId: string) => {
    onExistingFileSelect(itemId, index, fileId);
  };
  
  const handleLinkBlur = () => {
    if (link !== attachment.link) {
      onLinkUpdate(itemId, index, link);
    }
  };

  const handleDescriptionBlur = () => {
    if (description !== attachment.description) {
      onDescriptionUpdate(itemId, index, description);
    }
  };

  const getIcon = () => {
    switch (attachment.type) {
      case 'local-file': return documentOutline;
      case 'existing-resource': return folderOpenOutline;
      case 'link': return linkOutline;
      case 'image': return cameraOutline;
      default: return documentOutline;
    }
  };

  const renderLabelElement = () => {
    const baseStyle = {
      cursor: 'pointer',
      padding: '8px 12px',
      border: '1px solid var(--ion-color-medium)',
      borderRadius: '4px',
      backgroundColor: 'var(--ion-color-light)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      minHeight: '40px',
      fontSize: '14px',
    };

    switch (attachment.type) {
      case 'local-file':
        return (
          <>
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              accept="*/*"
              onChange={handleFileSelect}
            />
            <div
              style={{ ...baseStyle, opacity: readOnly ? 0.7 : 1, cursor: readOnly ? 'default' : 'pointer' }}
              onClick={() => {
                if (!readOnly) {
                  fileInputRef.current?.click();
                }
              }}
            >
              <IonIcon icon={getIcon()} />
              <IonText>
                {attachment.fileName || 'Select file...'}
              </IonText>
            </div>
          </>
        );

      case 'existing-resource':
        return (
          <IonSelect
            value={attachment.fileId}
            placeholder="Select existing file..."
            disabled={readOnly}
            onIonChange={(e) => handleExistingFileSelect(String(e.detail.value))}
            style={{
              ...baseStyle,
              cursor: readOnly ? 'default' : 'pointer',
              border: '1px solid var(--ion-color-medium)',
              opacity: readOnly ? 0.7 : 1,
              width: '100%',
              minWidth: 0,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {existingFiles.map((file) => (
              <IonSelectOption key={file.Id} value={file.Id.toString()}>
                {file.Name}
              </IonSelectOption>
            ))}
          </IonSelect>
        );

      case 'link':
        return (
          <IonInput
            value={link}
            placeholder="Enter URL..."
            disabled={readOnly}
            onIonInput={(e) => setLink(e.detail.value!)}
            onIonBlur={handleLinkBlur}
            style={{
              '--padding-start': '32px',
              '--padding-end': '12px',
              '--padding-top': '8px',
              '--padding-bottom': '8px',
              border: '1px solid var(--ion-color-medium)',
              borderRadius: '4px',
              opacity: readOnly ? 0.7 : 1,
            }}
          />
        );

      case 'image':
        return (
          <div
            style={{ ...baseStyle, opacity: readOnly ? 0.7 : 1, cursor: readOnly ? 'default' : 'pointer' }}
            onClick={() => {
              if (!readOnly) {
                handleTakePhoto();
              }
            }}
          >
            <IonIcon icon={getIcon()} />
            <IonText>
              {attachment.fileName || 'Capture Image...'}
            </IonText>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '12px',
        border: '1px solid var(--ion-color-light-shade)',
        borderRadius: '8px',
        backgroundColor: 'var(--ion-color-light-tint)',
        position: 'relative',
      }}
    >
      {!readOnly && (
        <IonIcon
          icon={closeOutline}
          onClick={() => onDelete(itemId, index)}
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            cursor: 'pointer',
            color: 'var(--ion-color-medium)',
            fontSize: '18px',
            padding: '4px',
            borderRadius: '50%',
            backgroundColor: 'var(--ion-color-light)',
            zIndex: 1,
          }}
        />
      )}

      {/* Responsive container for label and description */}
      <div
        className="attachment-responsive-container"
        style={{ marginRight: '24px' }}
      >
        {/* Label element */}
        <div className="attachment-label-wrapper">
          {renderLabelElement()}
        </div>

        {/* Description input */}
        <div className="attachment-description-wrapper">
          <IonTextarea
            value={description}
            placeholder="Enter description..."
            rows={2}
            disabled={readOnly}
            onIonInput={(e) => setDescription(e.detail.value!)}
            onIonBlur={handleDescriptionBlur}
            style={{
              '--padding-start': '8px',
              '--padding-end': '8px',
              '--padding-top': '8px',
              '--padding-bottom': '8px',
              border: '1px solid var(--ion-color-medium)',
              borderRadius: '4px',
              fontSize: '14px',
              width: '100%',
              opacity: readOnly ? 0.7 : 1,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default AttachmentComponent;